import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import KpiCard from "../../components/KpiCard/KpiCard";
import { Tag } from "../../components/Tag/Tag";

import { useToast } from "../../context/ToastContext";
import { getColorBaseTokenValue, hexToRgba } from "../../design-tokens";

import ProductionApi, { AccumulatedProductionResponse } from "../../services/ProductionApi";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import AccumulatedChart from "./AccumulatedChart";
import styles from "./ReportAccumulatedProduction.module.css";

const parseYmd = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const ReportAccumulatedProduction: React.FC = () => {
  const { t, i18n } = useTranslation("accumulatedProduction");
  const { displayToast } = useToast();
  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";

  const [data, setData] = useState<AccumulatedProductionResponse | null>(null);
  const [loading, setLoading] = useState(true); // load pendente no mount — evita flash do estado vazio
  const [lastUpdated, setLastUpdated] = useState("");

  const fmtNum = useCallback(
    (v: number): string =>
      Number.isInteger(v)
        ? Number(v).toLocaleString(locale)
        : Number(v).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    [locale],
  );
  const signed = useCallback((v: number): string => (v > 0 ? `+${fmtNum(v)}` : fmtNum(v)), [fmtNum]);
  const fmtDateFull = useCallback(
    (d: Date): string => d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }),
    [locale],
  );
  const fmtAxis = useCallback(
    (d: Date): string => d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit" }),
    [locale],
  );

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const resp = await ProductionApi.GetAccumulated();
      setData((resp?.data as AccumulatedProductionResponse) ?? null);
      setLastUpdated(new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }));
    } catch (err: any) {
      displayToast("error", t("loadErrorTitle"), err?.errors?.[0] ?? t("loadErrorMsg"), false, 6000);
    } finally {
      setLoading(false);
    }
  }, [displayToast, t, locale]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthName = useMemo(() => {
    if (!data) return "";
    return new Date(data.month.year, data.month.month - 1, 1).toLocaleDateString(locale, { month: "long", year: "numeric" });
  }, [data, locale]);

  const derived = useMemo(() => {
    if (!data) return null;
    const cumulative = data.cumulative ?? [];
    const projection = data.projection ?? [];
    const all = [...cumulative, ...projection];
    const cutIdx = cumulative.length - 1; // índice de "hoje" (último dia real)

    const rawDates = all.map((p) => p.date);
    const labels = all.map((p) => fmtAxis(parseYmd(p.date)));
    const realized = all.map((p, i) => (i < cumulative.length ? p.realizedAccum : null));
    const target = all.map((p) => p.targetAccum);
    // Só há projeção a desenhar quando o backend a habilita E devolve a cauda (vazia no fim do mês).
    const hasProjection = !!data.month.projectionAvailable && projection.length > 0;
    // Projetado: null em todo o trecho real; com projeção, o ponto de junção (hoje) + a cauda projetada.
    // Sem projeção o array fica todo null — nada de ponto/tooltip/legenda fantasma no dia de hoje.
    const projected = all.map((p, i) => {
      if (!hasProjection || i < cutIdx) return null;
      return p.realizedAccum;
    });

    const hasData =
      data.month.realized > 0 || data.day.realized > 0 || cumulative.some((p) => p.realizedAccum > 0);
    const projectedClose = hasProjection ? data.month.projectedClose : null;

    const attain = (realizedV: number, targetV: number): number => (targetV > 0 ? (realizedV / targetV) * 100 : 0);

    return {
      rawDates,
      labels,
      realized,
      target,
      projected,
      todayIndex: cutIdx,
      projectedClose,
      hasProjection,
      hasData,
      dayAttain: attain(data.day.realized, data.day.target),
      monthAttain: attain(data.month.realized, data.month.target),
    };
  }, [data, fmtAxis]);

  // Atualização em tempo real via SignalR (substitui o botão "Atualizar").
  useLiveRefresh(() => void load());

  const chartColors = useMemo(
    () => ({
      realized: getColorBaseTokenValue("color.primary.500"),
      realizedFill: hexToRgba(getColorBaseTokenValue("color.primary.500"), 0.12),
      target: getColorBaseTokenValue("color.neutral.500"),
      projected: getColorBaseTokenValue("color.feedback.warning.500"),
      todayLine: getColorBaseTokenValue("color.secondary.500"),
      lastPoint: getColorBaseTokenValue("color.primary.700"),
      text: getColorBaseTokenValue("color.neutral.500"),
      grid: getColorBaseTokenValue("color.neutral.100"),
    }),
    [],
  );

  const varianceCard = (title: string, variance: number, attainPct: number): React.ReactNode => (
    <KpiCard
      title={title}
      value={`${signed(variance)}`}
      icon="show_chart"
      variant={variance >= 0 ? "success" : "danger"}
      descriptionIcon={variance >= 0 ? "arrow_upward" : "arrow_downward"}
      trend={variance >= 0 ? "up" : "down"}
      description={`${fmtNum(Math.round(attainPct))}% ${t("ofTarget")}`}
    />
  );

  const livePill = (
    <span className={styles.pill}>
      <span className={styles.pillDot} />
      {t("live")}
    </span>
  );

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <Tag
          label={lastUpdated ? `${t("updatedAt")} ${lastUpdated}` : t("loadingLabel")}
          severity="successInverse"
          icon="sync"
        />
      </PageHeader>

      {loading && !data ? (
        <div className={`${styles.card} ${styles.loading}`}>
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
        </div>
      ) : data && derived && derived.hasData ? (
        <>
          {/* Bloco: Dia atual */}
          <section className={`${styles.card} ${styles.block}`}>
            <div className={styles.blockHeader}>
              <div className={styles.blockTitleWrap}>
                <span className={styles.blockTitle}>
                  <span className="material-symbols-outlined">today</span>
                  {t("dayBlock")}
                </span>
                <span className={styles.blockRange}>{fmtDateFull(parseYmd(data.day.date))}</span>
              </div>
              {livePill}
            </div>
            <div className={styles.kpiRow}>
              <KpiCard title={t("kRealized")} value={fmtNum(data.day.realized)} icon="inventory_2" variant="primary" description={t("unitEvents")} />
              <KpiCard title={t("kTargetDay")} value={fmtNum(data.day.target)} icon="flag" variant="info" description={t("unitEvents")} />
              {varianceCard(t("kVariance"), data.day.variance, derived.dayAttain)}
            </div>
          </section>

          {/* Bloco: Mês atual */}
          <section className={`${styles.card} ${styles.block}`}>
            <div className={styles.blockHeader}>
              <div className={styles.blockTitleWrap}>
                <span className={styles.blockTitle}>
                  <span className="material-symbols-outlined">calendar_month</span>
                  {t("monthBlock")}
                </span>
                <span className={styles.blockRange}>{monthName}</span>
              </div>
              {derived.hasProjection ? (
                <span className={styles.projBadge}>
                  <span className="material-symbols-outlined">trending_up</span>
                  {t("projClose")} {fmtNum(data.month.projectedClose)}
                  <span style={{ fontWeight: 400 }}>
                    · {t("projBasis", { n: data.month.projectionBasisDays })}
                  </span>
                </span>
              ) : (
                livePill
              )}
            </div>

            <div className={styles.kpiRow}>
              <KpiCard title={t("kRealized")} value={fmtNum(data.month.realized)} icon="inventory_2" variant="primary" description={t("unitEvents")} />
              <KpiCard title={t("kTargetMonth")} value={fmtNum(data.month.target)} icon="flag" variant="info" description={t("unitEvents")} />
              {varianceCard(t("kVariance"), data.month.variance, derived.monthAttain)}
            </div>

            {!data.month.projectionAvailable && (
              <div className={styles.warnBanner}>
                <span className="material-symbols-outlined">info</span>
                {t("warnNoProj")}
              </div>
            )}

            <div className={styles.chartBody}>
              <AccumulatedChart
                rawDates={derived.rawDates}
                labels={derived.labels}
                realized={derived.realized}
                target={derived.target}
                projected={derived.projected}
                todayIndex={derived.todayIndex}
                projectedClose={derived.projectedClose}
                colors={chartColors}
                locale={locale}
                labelsText={{
                  realized: t("legRealized"),
                  target: t("legTarget"),
                  projected: t("legProjected"),
                  today: t("todayLabel"),
                  unit: t("unitEvents"),
                  closePrefix: t("closePrefix"),
                }}
              />
            </div>

            <div className={styles.legend}>
              <span className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: chartColors.realized }} />
                {t("legRealized")}
              </span>
              <span className={styles.legendItem}>
                <span className={styles.legendLine} />
                {t("legTarget")}
              </span>
              {derived.projectedClose != null && (
                <span className={styles.legendItem}>
                  <span className={styles.legendLineProj} />
                  {t("legProjected")}
                </span>
              )}
            </div>
          </section>
        </>
      ) : (
        <div className={styles.card}>
          <div className={styles.empty}>
            <span className={`material-symbols-outlined ${styles.emptyIcon}`}>insights</span>
            <div>
              <div className={styles.emptyTitle}>{t("emptyTitle")}</div>
              <div className={styles.emptySub}>{t("emptySub")}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportAccumulatedProduction;
